import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Skeleton loading placeholder component.
 *
 * Use this for content loading states across the application.
 *
 * Variants:
 * - default: Standard gray pulsing skeleton
 * - card: Card-shaped skeleton with rounded corners
 * - text: Text-line shaped skeleton
 * - avatar: Circular avatar skeleton
 * - image: Image placeholder skeleton with aspect ratio
 *
 * @example
 * // Basic usage
 * <Skeleton className="h-4 w-[200px]" />
 *
 * // Card skeleton
 * <Skeleton variant="card" className="h-[200px]" />
 *
 * // Text lines
 * <div className="space-y-2">
 *   <Skeleton variant="text" className="w-full" />
 *   <Skeleton variant="text" className="w-3/4" />
 * </div>
 *
 * // Avatar
 * <Skeleton variant="avatar" size="lg" />
 */
const skeletonVariants = cva(
  "animate-pulse bg-muted",
  {
    variants: {
      variant: {
        default: "rounded-md",
        card: "rounded-lg",
        text: "h-4 rounded",
        avatar: "rounded-full",
        image: "rounded-lg aspect-video",
        button: "rounded-md h-10",
      },
      size: {
        default: "",
        sm: "h-8 w-8",
        md: "h-12 w-12",
        lg: "h-16 w-16",
        xl: "h-24 w-24",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(skeletonVariants({ variant, size, className }))}
        {...props}
      />
    )
  }
)
Skeleton.displayName = "Skeleton"

/**
 * Pre-built skeleton compositions for common use cases.
 */

/** Card skeleton with image, title, and description */
const SkeletonCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("space-y-3 rounded-lg border p-4", className)}
    {...props}
  >
    <Skeleton variant="image" className="w-full" />
    <div className="space-y-2">
      <Skeleton variant="text" className="w-3/4" />
      <Skeleton variant="text" className="w-full" />
      <Skeleton variant="text" className="w-1/2" />
    </div>
  </div>
))
SkeletonCard.displayName = "SkeletonCard"

/** Blog post card skeleton */
const SkeletonBlogCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("space-y-4 rounded-xl border p-4 shadow-xs", className)}
    {...props}
  >
    <Skeleton variant="image" className="w-full h-48" />
    <div className="space-y-2">
      <div className="flex gap-2">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
      <Skeleton variant="text" className="w-full h-6" />
      <Skeleton variant="text" className="w-full" />
      <Skeleton variant="text" className="w-2/3" />
    </div>
    <div className="flex items-center gap-3">
      <Skeleton variant="avatar" size="sm" />
      <div className="space-y-1 flex-1">
        <Skeleton variant="text" className="w-24" />
        <Skeleton variant="text" className="w-16 h-3" />
      </div>
    </div>
  </div>
))
SkeletonBlogCard.displayName = "SkeletonBlogCard"

/** Product card skeleton */
const SkeletonProductCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("space-y-3 rounded-lg border p-4", className)}
    {...props}
  >
    <Skeleton variant="image" className="w-full h-40" />
    <Skeleton variant="text" className="w-3/4 h-5" />
    <Skeleton variant="text" className="w-1/2" />
    <div className="flex justify-between items-center pt-2">
      <Skeleton className="h-6 w-16" />
      <Skeleton variant="button" className="w-24" />
    </div>
  </div>
))
SkeletonProductCard.displayName = "SkeletonProductCard"

/** Tool result skeleton */
const SkeletonToolResult = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("space-y-4 rounded-xl border p-6", className)}
    {...props}
  >
    <div className="flex items-center gap-3">
      <Skeleton variant="avatar" size="md" />
      <div className="space-y-2 flex-1">
        <Skeleton variant="text" className="w-1/3 h-6" />
        <Skeleton variant="text" className="w-1/2" />
      </div>
    </div>
    <Skeleton className="h-px w-full" />
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton variant="text" className="w-1/2 h-3" />
          <Skeleton variant="text" className="w-full h-5" />
        </div>
      ))}
    </div>
  </div>
))
SkeletonToolResult.displayName = "SkeletonToolResult"

export {
  Skeleton,
  SkeletonCard,
  SkeletonBlogCard,
  SkeletonProductCard,
  SkeletonToolResult,
  skeletonVariants,
}
